import type { RideTraceSample } from '../ride/runner.ts';

export interface ReplaySamplingOptions {
  readonly trackLength?: number;
  readonly closed?: boolean;
}

function interpolateDistance(
  from: number,
  to: number,
  alpha: number,
  options: ReplaySamplingOptions,
): number {
  const trackLength = options.trackLength ?? 0;
  let delta = to - from;
  if (options.closed && trackLength > 0 && Math.abs(delta) > trackLength * 0.5)
    delta += delta > 0 ? -trackLength : trackLength;
  const distance = from + delta * alpha;
  if (!options.closed || trackLength <= 0) return distance;
  return ((distance % trackLength) + trackLength) % trackLength;
}

export function sampleReplayTraceAtTime(
  trace: readonly RideTraceSample[],
  time: number,
  options: ReplaySamplingOptions = {},
): RideTraceSample | null {
  if (trace.length === 0) return null;
  const first = trace[0]!;
  const last = trace.at(-1)!;
  if (time <= first.time) return first;
  if (time >= last.time) return last;

  let low = 0;
  let high = trace.length - 1;
  while (low + 1 < high) {
    const midpoint = Math.floor((low + high) * 0.5);
    if (trace[midpoint]!.time <= time) low = midpoint;
    else high = midpoint;
  }

  const from = trace[low]!;
  const to = trace[high]!;
  const duration = Math.max(1e-9, to.time - from.time);
  const alpha = Math.max(0, Math.min(1, (time - from.time) / duration));
  const nearest = alpha < 0.5 ? from : to;
  const lerp = (start: number, end: number) => start + (end - start) * alpha;
  return {
    time,
    distance: interpolateDistance(from.distance, to.distance, alpha, options),
    speed: lerp(from.speed, to.speed),
    direction: nearest.direction,
    verticalG: lerp(from.verticalG, to.verticalG),
    lateralG: lerp(from.lateralG, to.lateralG),
    longitudinalG: lerp(from.longitudinalG, to.longitudinalG),
    sectionType: nearest.sectionType,
  };
}
